<template>
    <div>
        <table class="table table-striped table-class customer-highlight-table hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th
                        v-for="(header, idx) in headers"
                        :key="idx"
                        @click="sortBy(idx, header.type)"
                        class="dashboard-sort-header"
                    >
                        <span v-if="sortColumn == idx">
                            <font-awesome-icon
                                v-if="sortDirection != 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection != 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        {{ header.label }}
                    </th>

                    <th scope="col" class="dashboard-sort-header">ACT BTN</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="item in paginatedData"
                    :key="item.id"
                    :class="rowClass(item)"
                >
                    <td>{{ formatComponentControlDate(item.post_date) }}</td>
                    <td :class="customerCellClass(item.bill_name)">
                        {{ item.bill_name || '-' }}
                    </td>
                    <td>
                        <PartNumberDisplay
                            :part-number="item.part_number"
                            :selected-part="part_selected"
                        />
                    </td>
                    <td>
                        {{ item.customer_pn }}
                    </td>
                    <td>
                        <QuoteDescription
                            :description="item.description"
                            @open-description="$refs.descriptionModal.open($event)"
                        />
                    </td>
                    <td>{{ item.condition_code || '-' }}</td>
                    <td>{{ formatNumeric(item.qty_invoiced) }}</td>
                    <td>{{ formatNumeric(item.unit_cost, 'money') }}</td>
                    <td>{{ formatNumeric(item.total_cost, 'money') }}</td>
                    <td>{{ formatNumeric(item.unit_price, 'money') }}</td>
                    <td>{{ formatNumeric(item.total_price, 'money') }}</td>
                    <td>{{ formatNumeric(item.profit, 'money') }}</td>
                    <td>{{ formatNumeric(item.margin, 'percentWhole') }}</td>
                    <td>
                        <a
                            href="javascript:;"
                            @click="triggerMoreDetails(item)"
                            class="mx-2 dashboard-action-link"
                            title="More details"
                        >
                            <font-awesome-icon :icon="['fas', 'eye']" />
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>

        <QuoteDescriptionModal ref="descriptionModal" title="Sales description" />

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1"
                >
                    Previous
                </button>

                <span class="mx-3">
                    Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
                </span>

                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia'
    import { formatComponentControlDate } from '@/helpers/dates.js'
    import { formatNumeric, normalizePart, parseNumeric } from '@/helpers/string.js'
    import {
        buildDashboardRowHighlightClass,
        getDashboardCustomerCellClass,
    } from '@/helpers/dashboardRowHighlight.js'
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import QuoteDescription from '@/components/shared/QuoteDescription.vue'
    import QuoteDescriptionModal from '@/components/modals/QuoteDescriptionModal.vue'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    export default {
        name: 'SalesTable',
        components: {
            PartNumberDisplay,
            QuoteDescription,
            QuoteDescriptionModal,
        },
        emits: ['more-details'],
        props: {
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
            data: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                headers: [
                    { label: 'Post Date', type: 'date' },
                    { label: 'Customer', type: 'string' },
                    { label: 'Part Number', type: 'string' },
                    { label: 'Customer P/N', type: 'string' },
                    { label: 'Description', type: 'string' },
                    { label: 'Cond code', type: 'string' },
                    { label: 'Qty', type: 'numeric' },
                    { label: 'Unit Cost', type: 'numeric' },
                    { label: 'Total Cost', type: 'numeric' },
                    { label: 'Unit Price', type: 'numeric' },
                    { label: 'Total Price', type: 'numeric' },
                    { label: 'Profit', type: 'numeric' },
                    { label: 'Mrgn', type: 'numeric' },
                ],
                sortColumn: 0,
                columnType: 'date',
                sortDirection: 'desc',
                currentPage: 1,
                itemsPerPage: 10,
            }
        },
        computed: {
            ...mapState(useSubstitutesStore, ['selectedSubstituteOnModal']),
            sortedData() {
                return this.data.slice().sort((a, b) => {
                    const key = this.getColumnKey(this.sortColumn)
                    let valueA = a[key]
                    let valueB = b[key]

                    if (this.columnType === 'numeric') {
                        valueA = parseNumeric(valueA, 0)
                        valueB = parseNumeric(valueB, 0)
                    } else if (this.columnType === 'date') {
                        valueA = formatComponentControlDate(valueA)
                        valueB = formatComponentControlDate(valueB)
                    } else {
                        valueA = String(valueA).toLowerCase()
                        valueB = String(valueB).toLowerCase()
                    }

                    return this.sortDirection === 'asc'
                        ? valueA > valueB
                            ? 1
                            : -1
                        : valueA < valueB
                          ? 1
                          : -1
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedData.slice(start, end)
            },
            totalPages() {
                return Math.ceil(this.data.length / this.itemsPerPage)
            },
        },
        watch: {
            data: {
                handler() {
                    this.applyDefaultSort()
                },
                immediate: true,
            },
        },
        methods: {
            formatComponentControlDate,
            formatNumeric,
            applyDefaultSort() {
                this.sortColumn = 0
                this.columnType = 'date'
                this.sortDirection = 'desc'
                this.currentPage = 1
            },
            triggerMoreDetails(details) {
                this.$emit('more-details', details)
            },
            sortBy(columnIndex, columnType) {
                this.columnType = columnType

                if (this.sortColumn === columnIndex) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = columnIndex
                    this.sortDirection = 'asc'
                }
            },
            getColumnKey(index) {
                const keys = [
                    'post_date',
                    'bill_name',
                    'part_number',
                    'customer_pn',
                    'description',
                    'condition_code',
                    'qty_invoiced',
                    'unit_cost',
                    'total_cost',
                    'unit_price',
                    'total_price',
                    'profit',
                    'margin',
                ]
                return keys[index]
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            isActivePart(partNumber) {
                if (!this.selectedSubstituteOnModal || partNumber == null) return false
                const selected = normalizePart(this.selectedSubstituteOnModal)
                const current = normalizePart(partNumber)
                return selected.length > 0 && selected === current
            },
            rowClass(item) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    customerName: item?.bill_name,
                    selectedPart: this.part_selected,
                    partNumber: item?.part_number,
                    activePart: this.isActivePart(item.part_number),
                })
            },
            customerCellClass(customerName) {
                return getDashboardCustomerCellClass(this.highlightedCustomer, customerName)
            },
        },
    }
</script>

<style scoped>
    .table thead td,
    .table thead th {
        font-size: 15px;
        font-weight: 700;
        text-transform: uppercase;
    }
</style>
